import { useState, useEffect } from 'react';
import { X, Mail, Phone, MapPin, Building2, Percent, CheckCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';

interface FactoringCompany {
  id: string;
  company_id: string;
  name: string;
  email: string;
  phone: string;
  contact_phone_ext?: string;
  contact_person?: string;
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  automatic_document_transfer?: boolean;
  load_factoring_fee_percentage?: number;
  notes?: string;
  created_at: string;
  updated_at: string;
}

interface ViewFactoringCompanyModalProps {
  companyId: string;
  onClose: () => void;
  onEdit: () => void;
}

export function ViewFactoringCompanyModal({ companyId, onClose, onEdit }: ViewFactoringCompanyModalProps) {
  const toast = useToast();
  const [company, setCompany] = useState<FactoringCompany | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCompany();
  }, [companyId]);

  async function loadCompany() {
    try {
      const { data, error } = await supabase
        .from('factoring_companies')
        .select('*')
        .eq('id', companyId)
        .single();

      if (error) throw error;
      setCompany(data);
    } catch (error) {
      console.error('Error loading company:', error);
      toast.error('Failed to load factoring company details');
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full p-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!company) {
    return null;
  }

  const InfoRow = ({ label, value, icon: Icon }: { label: string; value?: string | number; icon?: any }) => {
    if (!value && value !== 0) return null;
    
    return (
      <div className="flex items-start gap-3 py-2">
        {Icon && <Icon className="w-5 h-5 text-slate-400 mt-0.5" />}
        <div className="flex-1">
          <p className="text-sm font-medium text-slate-500">{label}</p>
          <p className="text-base text-slate-900">{value}</p>
        </div>
      </div>
    );
  };

  const fullAddress = [
    company.street,
    company.city && company.state && company.zip ? `${company.city}, ${company.state} ${company.zip}` : null
  ].filter(Boolean).join('\n');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-blue-600 to-blue-700 text-white">
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <Building2 className="w-8 h-8" />
              <div>
                <h2 className="text-2xl font-bold">{company.name}</h2>
                <p className="text-sm text-blue-100 mt-1">
                  Company #{company.company_id}
                </p>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Automatic Document Transfer Badge */}
          {company.automatic_document_transfer && (
            <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg p-4">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-sm font-semibold text-green-800">
                Automatic Document Transfer Enabled
              </span>
            </div>
          )}

          {/* Contact Information */}
          <div className="bg-slate-50 rounded-lg p-6 space-y-4">
            <h3 className="text-lg font-semibold text-slate-900 border-b border-slate-200 pb-2">
              Contact Information
            </h3>
            <div className="space-y-3">
              <InfoRow label="Contact Person" value={company.contact_person} icon={Building2} />
              <InfoRow label="Email" value={company.email} icon={Mail} />
              <InfoRow 
                label="Phone" 
                value={company.phone ? 
                  `${company.phone}${company.contact_phone_ext ? ` ext. ${company.contact_phone_ext}` : ''}` 
                  : undefined
                } 
                icon={Phone} 
              />
            </div>
          </div>

          {/* Address */}
          {fullAddress && (
            <div className="bg-slate-50 rounded-lg p-6 space-y-4">
              <h3 className="text-lg font-semibold text-slate-900 border-b border-slate-200 pb-2">
                Address
              </h3>
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-slate-400 mt-0.5" />
                <div className="flex-1">
                  <p className="text-slate-900 whitespace-pre-line">{fullAddress}</p>
                </div>
              </div>
            </div>
          )}

          {/* Load Factoring Fee */}
          {company.load_factoring_fee_percentage && (
            <div className="bg-blue-50 rounded-lg p-6 space-y-4 border border-blue-200">
              <h3 className="text-lg font-semibold text-slate-900 border-b border-blue-200 pb-2 flex items-center gap-2">
                <Percent className="w-5 h-5 text-blue-600" />
                Load Factoring Fee
              </h3>
              <div className="flex items-center gap-3">
                <div className="text-4xl font-bold text-blue-600">
                  {company.load_factoring_fee_percentage}%
                </div>
                <div className="text-sm text-slate-600">
                  Fee per load, categorized as expense in General Ledger
                </div>
              </div>
            </div>
          )}

          {/* Notes */}
          {company.notes && (
            <div className="bg-slate-50 rounded-lg p-6 space-y-4">
              <h3 className="text-lg font-semibold text-slate-900 border-b border-slate-200 pb-2">
                Notes
              </h3>
              <p className="text-slate-700 whitespace-pre-wrap">{company.notes}</p>
            </div>
          )}

          {/* Metadata */}
          <div className="bg-slate-50 rounded-lg p-6 space-y-2">
            <h3 className="text-lg font-semibold text-slate-900 border-b border-slate-200 pb-2">
              Record Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-slate-500">Created</p>
                <p className="text-slate-900 font-medium">
                  {new Date(company.created_at).toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-slate-500">Last Updated</p>
                <p className="text-slate-900 font-medium">
                  {new Date(company.updated_at).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-slate-200 bg-slate-50 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-6 py-2.5 border border-slate-300 rounded-lg hover:bg-white transition-colors font-medium"
          >
            Close
          </button>
          <button
            onClick={onEdit}
            className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
          >
            Edit Company
          </button>
        </div>
      </div>
    </div>
  );
}
